import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiSave, FiUser, FiPhone, FiMail, FiMapPin, FiLock, FiEye, FiEyeOff, FiDollarSign } from 'react-icons/fi';

const API_URL = process.env.REACT_APP_API_URL || 'https://green-admin.merket.io/api';

const ProfileManagement = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Get user info from localStorage
  const userName = localStorage.getItem('userName') || 'Admin';
  const userRole = localStorage.getItem('userRole') || 'admin';
  const adminId = localStorage.getItem('adminId'); // null for env admin

  // Profile form
  const [profileForm, setProfileForm] = useState({
    fullName: '',
    phone: '',
    email: '',
    address: ''
  });

  // Password form
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Salary history state (for sales role)
  const [salaryHistory, setSalaryHistory] = useState({ expenses: [], total: 0, loading: false });
  const [showSalaryHistory, setShowSalaryHistory] = useState(false);

  useEffect(() => {
    loadProfile();
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadProfile = async () => {
    if (!adminId) {
      // Env admin - no profile to load
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/shop-settings`);
      if (response.data.success && response.data.data?.additionalAdmins) {
        const admin = response.data.data.additionalAdmins.find(a => a._id === adminId);
        if (admin) {
          setProfileForm({
            fullName: admin.fullName || '',
            phone: admin.phone || '',
            email: admin.email || '',
            address: admin.address || ''
          });
        }
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSalaryHistory = async () => {
    if (salaryHistory.expenses.length > 0) {
      // Already loaded, just toggle visibility
      setShowSalaryHistory(!showSalaryHistory);
      return;
    }

    setSalaryHistory(prev => ({ ...prev, loading: true }));
    setShowSalaryHistory(true);

    try {
      // Build recipient name to search
      const recipientName = profileForm.fullName 
        ? `${profileForm.fullName} - ${userName}` 
        : userName;
      
      const response = await axios.get(`${API_URL}/expenses/by-recipient/${encodeURIComponent(recipientName)}`);
      
      if (response.data.success) {
        setSalaryHistory({
          expenses: response.data.data || [],
          total: response.data.total || 0,
          loading: false
        });
      } else {
        setSalaryHistory(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('Error loading salary history:', error);
      setSalaryHistory(prev => ({ ...prev, loading: false }));
      toast.error('Không thể tải lịch sử chi lương');
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!adminId) {
      toast.info('Tài khoản chính không thể cập nhật thông tin');
      return;
    }

    try {
      setSaving(true);
      const response = await axios.put(`${API_URL}/shop-settings/admins/${adminId}/profile`, profileForm);
      if (response.data.success) {
        toast.success('Đã cập nhật thông tin');
      }
    } catch (error) {
      toast.error('Không thể cập nhật thông tin');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Mật khẩu mới không khớp');
      return;
    }

    if (passwordForm.newPassword.length < 4) {
      toast.error('Mật khẩu mới phải có ít nhất 4 ký tự');
      return;
    }

    if (!adminId) {
      toast.info('Tài khoản chính không thể đổi mật khẩu tại đây');
      return;
    }

    try {
      setChangingPassword(true);
      const response = await axios.put(`${API_URL}/shop-settings/admins/${adminId}`, {
        password: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      
      if (response.data.success) {
        toast.success('Đổi mật khẩu thành công');
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowPasswordSection(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Không thể đổi mật khẩu');
    } finally {
      setChangingPassword(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('vi-VN');
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Đang tải...</div>;
  }

  return (
    <div className={`${isMobile ? 'pb-20' : ''}`}>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Thông tin cá nhân</h1>

      {/* User Info Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <FiUser className="text-green-600" size={32} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">{userName}</h2>
            <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mt-1 ${
              userRole === 'sales' 
                ? 'bg-blue-100 text-blue-700' 
                : 'bg-green-100 text-green-700'
            }`}>
              {userRole === 'sales' ? 'Nhân viên bán hàng' : 'Quản trị viên'}
            </span>
          </div>
        </div>
      </div>

      {/* Profile Form */}
      {adminId ? (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">Thông tin liên hệ</h3>
          <form onSubmit={handleSaveProfile} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FiUser className="inline mr-2" />
                Họ và tên
              </label>
              <input
                type="text"
                value={profileForm.fullName}
                onChange={(e) => setProfileForm({ ...profileForm, fullName: e.target.value })}
                className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Nhập họ và tên"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FiPhone className="inline mr-2" />
                Số điện thoại
              </label>
              <input
                type="tel"
                value={profileForm.phone}
                onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Nhập số điện thoại"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FiMail className="inline mr-2" />
                Email
              </label>
              <input
                type="email"
                value={profileForm.email}
                onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Nhập email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FiMapPin className="inline mr-2" />
                Địa chỉ
              </label>
              <input
                type="text"
                value={profileForm.address}
                onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
                className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Nhập địa chỉ"
              />
            </div>

            <button
              type="submit"
              disabled={saving}
              className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
            >
              <FiSave className="mr-2" />
              {saving ? 'Đang lưu...' : 'Lưu thông tin'}
            </button>
          </form>
        </div>
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-yellow-800">
            Bạn đang đăng nhập bằng tài khoản quản trị chính. Thông tin cá nhân không khả dụng.
          </p>
        </div>
      )}

      {/* Change Password Section */}
      {adminId && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Đổi mật khẩu</h3>
            <button
              onClick={() => setShowPasswordSection(!showPasswordSection)}
              className="text-green-600 hover:text-green-700 text-sm font-medium"
            >
              {showPasswordSection ? 'Ẩn' : 'Hiện'}
            </button>
          </div>

          {showPasswordSection && (
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mật khẩu hiện tại
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    className="w-full border rounded-lg px-4 py-2 pr-10 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    {showCurrentPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mật khẩu mới
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    className="w-full border rounded-lg px-4 py-2 pr-10 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    required
                    minLength={4}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    {showNewPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Xác nhận mật khẩu mới
                </label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                />
                {passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword && (
                  <p className="text-red-500 text-sm mt-1">Mật khẩu không khớp</p>
                )}
              </div>

              <button
                type="submit"
                disabled={changingPassword || passwordForm.newPassword !== passwordForm.confirmPassword}
                className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
              >
                <FiLock className="mr-2" />
                {changingPassword ? 'Đang xử lý...' : 'Đổi mật khẩu'}
              </button>
            </form>
          )}
        </div>
      )}

      {/* Salary History Section - Only for sales role */}
      {adminId && userRole === 'sales' && (
        <div className="bg-white rounded-lg shadow p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center">
              <FiDollarSign className="mr-2 text-orange-600" />
              Lịch sử chi lương
            </h3>
            <button
              onClick={loadSalaryHistory}
              className="text-orange-600 hover:text-orange-700 text-sm font-medium"
            >
              {showSalaryHistory ? 'Ẩn' : 'Xem'}
            </button>
          </div>

          {showSalaryHistory && (
            <>
              {salaryHistory.loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
                  <p className="text-gray-500 mt-2">Đang tải...</p>
                </div>
              ) : salaryHistory.expenses.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FiDollarSign size={48} className="mx-auto mb-2 opacity-30" />
                  <p>Chưa có khoản chi lương nào</p>
                </div>
              ) : (
                <>
                  {/* Summary */}
                  <div className="mb-4 p-4 bg-green-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-green-800 font-medium">Tổng đã nhận:</span>
                      <span className="text-green-800 font-bold text-xl">{formatCurrency(salaryHistory.total)}</span>
                    </div>
                    <p className="text-green-600 text-sm mt-1">{salaryHistory.expenses.length} lần chi</p>
                  </div>

                  {/* Expenses List */}
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {salaryHistory.expenses.map((expense, index) => (
                      <div key={expense._id || index} className="border rounded-lg p-3 hover:bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-medium">{expense.name}</p>
                            <p className="text-sm text-gray-500">
                              {formatDate(expense.expenseDate)}
                              {expense.expenseGroup?.name && (
                                <span className="ml-2 px-2 py-0.5 bg-gray-100 rounded text-xs">
                                  {expense.expenseGroup.name}
                                </span>
                              )}
                            </p>
                            {expense.notes && (
                              <p className="text-sm text-gray-400 mt-1">{expense.notes}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-orange-600">{formatCurrency(expense.amount)}</p>
                            <p className="text-xs text-gray-400">
                              {expense.paymentMethod === 'cash' ? 'Tiền mặt' : 'Chuyển khoản'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileManagement;
